/*  AnnouncementBar — top-of-page marketing strip.
    ────────────────────────────────────────────────────────────────
    iter100.147 — Fully CMS-driven. Reads `announcement_bar.*` keys
    from /api/landing/cms/announcement_bar:

        enabled       — 'true' / 'false' (string) → master on/off.
        message       — banner text (<=90 chars ideal).
        link_href     — optional internal link. Blank = no link.
        link_label    — small pill CTA label ("Start now").
        tint          — info | success | warning | rose | brand.
        dismissible   — 'true' / 'false'. If 'true', a small × lets
                        the visitor hide it (remembered per-message
                        in localStorage so a new message reappears).
        expires_at    — optional ISO date (YYYY-MM-DD). Blank = never.

    Rendered above the sticky Header from App.js so it sits at the
    absolute top of every page. The Header stays sticky-below and
    scrolls independently.
*/
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "@phosphor-icons/react";
import { useCms } from "@/lib/useCms";


const TINT_CLASSES = {
    info:    "bg-primary text-white",
    success: "bg-emerald-600 text-white",
    warning: "bg-amber-500 text-white",
    rose:    "bg-rose-600 text-white",
    brand:   "bg-slate-900 text-white",
};


function hashMessage(msg) {
    // Cheap stable key for localStorage — visitors who dismiss one
    // banner still see the next one when copy changes.
    let h = 0;
    for (let i = 0; i < (msg || "").length; i++) {
        h = ((h << 5) - h) + msg.charCodeAt(i);
        h |= 0;
    }
    return `announcement:${h}`;
}


export default function AnnouncementBar() {
    const { t, loading } = useCms("announcement_bar");
    const [dismissed, setDismissed] = useState(false);

    const enabled     = String(t("enabled", "false")).toLowerCase() === "true";
    const message     = t("message", "");
    const linkHref    = t("link_href", "");
    const linkLabel   = t("link_label", "");
    const tint        = t("tint", "info");
    const dismissible = String(t("dismissible", "true")).toLowerCase() === "true";
    const expiresAt   = t("expires_at", "");

    const storageKey = hashMessage(message);

    useEffect(() => {
        if (!dismissible) return;
        setDismissed(!!window.localStorage.getItem(storageKey));
    }, [storageKey, dismissible]);

    if (loading || !enabled || !message) return null;

    // Auto-hide once past the expiry date.
    if (expiresAt) {
        const exp = new Date(expiresAt);
        if (!isNaN(exp.getTime()) && exp.getTime() < Date.now()) return null;
    }

    if (dismissible && dismissed) return null;

    const tintClass = TINT_CLASSES[tint] || TINT_CLASSES.info;

    const handleDismiss = () => {
        if (!dismissible) return;
        window.localStorage.setItem(storageKey, "1");
        setDismissed(true);
    };

    return (
        <div className={`${tintClass} relative`} data-testid="announcement-bar">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 py-2.5 flex items-center justify-center gap-3 text-sm">
                <span className="font-medium leading-snug" data-testid="announcement-bar-message">{message}</span>
                {linkHref && linkLabel && (
                    <Link
                        to={linkHref}
                        className="inline-flex items-center gap-1 bg-white/15 hover:bg-white/25 backdrop-blur px-2.5 py-1 rounded-full text-xs font-semibold transition-colors"
                        data-testid="announcement-bar-link"
                    >
                        {linkLabel}
                        <ArrowRight size={11} weight="bold" />
                    </Link>
                )}
            </div>
            {dismissible && (
                <button
                    type="button"
                    onClick={handleDismiss}
                    className="absolute top-1/2 -translate-y-1/2 right-3 sm:right-5 opacity-80 hover:opacity-100 transition-opacity"
                    aria-label="Dismiss announcement"
                    data-testid="announcement-bar-dismiss"
                >
                    <X size={14} weight="bold" />
                </button>
            )}
        </div>
    );
}
